import { useContext, useEffect, useState } from 'react';
import { useHttp, useNotification } from 'src/common/hooks';
import { Club } from 'src/common/types/clubs';
import { Event } from 'src/common/types/event';
import { formatDateTime, getInitials, queryParams } from 'src/common/utils';
import { AuthContext } from 'src/contexts';

export const SuggestEventPopup = ({ onClose, club, onSuggest }: SuggestEventPopupProps) => {
  const notify = useNotification();
  const { accessToken } = useContext(AuthContext);
  const [events, setEvents] = useState<Event[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);

  const { sendRequest: getEvents, isLoading: isGettingEvents } = useHttp();

  const { sendRequest: suggestEvents, isLoading: isSuggestingEvents } = useHttp();

  useEffect(() => {
    if (accessToken) {
      getEventsHandler();
    }
  }, [accessToken, searchValue]);

  const getEventsHandler = async () => {
    const params = {
      search: searchValue,
    };

    await getEvents(
      {
        url: 'events?' + queryParams(params),
        method: 'GET',
        accessToken,
      },
      (res: any) => {
        setEvents(res.data);
      },
    );
  };

  const handleSuggest = () => {
    if (selectedEvents.length) {
      suggestEvents(
        {
          url: 'clubs/attach-events',
          method: 'POST',
          accessToken,
          payload: {
            clubId: club.id,
            eventIds: selectedEvents,
          },
        },
        handleSuggestResponse,
      );
    } else {
      notify('Please select at least one event to suggest.', 'error');
    }
  };

  const handleSuggestResponse = () => {
    notify(`Your suggested events are now part of ${club.title}`, 'success');

    setTimeout(() => {
      onSuggest();
    }, 1000);
  };

  const handleEventSelection = (eventId: string | undefined) => {
    if (eventId && typeof eventId === 'string') {
      if (selectedEvents.includes(eventId)) {
        setSelectedEvents(selectedEvents.filter(id => id !== eventId));
      } else {
        setSelectedEvents([...selectedEvents, eventId]);
      }
    }
  };

  return (
    <div className="fixed top-0 left-0 w-full h-full flex items-center justify-center bg-[#0B1444E5] bg-opacity-90 z-50 flex-col">
      <div className="w-[490px] relative bg-white rounded-lg p-14 shadow-lg flex flex-col custom-scrollbar-1 overflow-y-scroll">
        <button className="absolute right-3 top-3" onClick={onClose}>
          <svg
            width="43"
            height="42"
            viewBox="0 0 43 42"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0.5 15C0.5 6.71573 7.21573 0 15.5 0H27.5C35.7843 0 42.5 6.71573 42.5 15V27C42.5 35.2843 35.7843 42 27.5 42H15.5C7.21573 42 0.5 35.2843 0.5 27V15Z"
              fill="#E5E5E5"
            />
            <path d="M17.5 17L25.5 25" stroke="black" strokeWidth="2" strokeLinecap="round" />
            <path d="M25.5 17L17.5 25" stroke="black" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        <h2 className="text-[34px] font-bold font-[sohne-breit] tracking-wide mb-10 leading-none">
          Suggest one or more events
        </h2>

        <div className="grid gap-5">
          <input
            type="text"
            name="title"
            placeholder="Start typing to search for an event..."
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            className={`w-full rounded-md h-[60px] border-gray-300 focus:border-gray-300 mb !border-b-[#005AFF]`}
          />

          <div className="max-h-[300px] overflow-y-scroll custom-scrollbar">
            {events
              .filter(event => !club?.events?.find(clubEvent => clubEvent.event.id === event.id))
              .map((event, index) => (
                <div
                  className="flex justify-between items-center py-[17px] border-t border-[#E5E5E5] last:border-b"
                  key={index}
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={selectedEvents.includes(event.id)}
                      onChange={() => handleEventSelection(event.id)}
                      className="rounded-full h-[26px] w-[26px] text-[#F35800] !bg-[length:80%_80%]"
                    />

                    <div className="flex flex-col">
                      <span className="mr-1 font-bold max-w-[240px] truncate">{event.title}</span>

                      <span className="text-sm text-[#000000B2]">
                        {formatDateTime(event.startTime)}
                      </span>
                    </div>
                  </div>

                  <div className="flex">
                    {event.hosts.slice(0, 4).map(member => (
                      <div
                        className="rounded-full  h-[26px] w-[26px] -ml-2.5 first:ml-0 flex items-center justify-center bg-blue-400 text-xs"
                        key={member.id}
                      >
                        {member.user.avatar ? (
                          <div className="flex items-center justify-center w-full h-full">
                            <img
                              src={member.user.avatar}
                              className="object-cover w-full h-full rounded-full"
                              alt="Profile Avatar"
                            />
                          </div>
                        ) : (
                          <div>{getInitials(member.user.name)}</div>
                        )}
                      </div>
                    ))}

                    {event.hosts.length > 4 && (
                      <div className="rounded-full h-7 min-w-7 -ml-2.5 bg-[#636989E5] text-white text-[12px] flex items-center justify-center p-1.5 font-bold">
                        +{event.hosts.length - 4}
                      </div>
                    )}
                  </div>
                </div>
              ))}

            {!events.filter(
              event => !club?.events?.find(clubEvent => clubEvent.event.id === event.id),
            ).length &&
              !isGettingEvents && (
                <div className="py-[17px] border-t border-[#E5E5E5] last:border-b text-center text-[#000000B2]">
                  <span className="mr-1 font-bold">No events found.</span>
                </div>
              )}

            {isGettingEvents && !events.length && (
              <div role="status" className="mx-auto w-fit">
                <svg
                  aria-hidden="true"
                  className="inline w-10 h-10 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                  />
                </svg>

                <span className="sr-only">Loading...</span>
              </div>
            )}
          </div>

          <div className="flex gap-6 mt-5">
            <button
              onClick={handleSuggest}
              disabled={isSuggestingEvents}
              className="px-8 py-[21px] text-xs text-center font-medium font-[sohne-breit] tracking-wider text-white bg-[#005AFF] rounded-lg focus:outline-none hover:bg-opacity-90 transition-opacity"
            >
              Suggest selected
            </button>

            <button onClick={onClose} className="text-black underline text-opacity-70">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface SuggestEventPopupProps {
  onClose: () => void;
  onSuggest: () => void;
  club: Club;
}
